import React from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Reusable DonutChart component.
 *
 * Props:
 *  - labels: string[]
 *  - data: number[]
 *  - colors?: string[]         — background colors per slice
 *  - centerLabel?: string      — text shown inside the donut ring
 *  - height?: number           — px height of the donut (default 200)
 *  - legendItems?: Array<{ label: string, value: string, color: string, details?: string }> — below-chart legend
 */
const DonutChart = ({
    labels = [],
    data = [],
    colors,
    centerLabel,
    height = 200,
    legendItems,
}) => {
    const defaultColors = [
        "#6366F1",
        "#8B5CF6",
        "#EC4899",
        "#10B981",
        "#3B82F6",
        "#F59E0B",
        "#F97316",
    ];

    const chartData = {
        labels,
        datasets: [
            {
                data,
                backgroundColor: colors || defaultColors.slice(0, data.length),
                borderWidth: 3,
                borderColor: "#fff",
                hoverBorderWidth: 4,
                hoverOffset: 4,
            },
        ],
    };

    // Plugin to draw center text
    const centerTextPlugin = {
        id: "centerText",
        afterDraw(chart) {
            const { width, height, ctx } = chart;
            const centerLabel = chart.config.options.plugins.centerText?.label;
            if (!centerLabel) return;

            ctx.save();
            const fontSize = (height / 110).toFixed(2);
            ctx.font = `bold ${fontSize}em Inter, sans-serif`;
            ctx.textBaseline = "middle";
            ctx.textAlign = "center";
            ctx.fillStyle = "#111827";
            
            const lines = String(centerLabel).split("\n");
            const lineHeight = 18;
            const totalHeight = lines.length * lineHeight;
            const startY = height / 2 - (totalHeight / 2) + (lineHeight / 2);

            lines.forEach((line, i) => {
                ctx.fillText(line, width / 2, startY + i * lineHeight);
            });
            ctx.restore();
        },
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "72%",
        plugins: {
            legend: { display: false },
            centerText: { label: centerLabel },
            tooltip: {
                backgroundColor: "#1F2937",
                titleFont: { size: 12, family: "'Inter', sans-serif" },
                bodyFont: { size: 11, family: "'Inter', sans-serif" },
                padding: 10,
                cornerRadius: 8,
                callbacks: {
                    title: (contexts) => contexts[0]?.label || "",
                    label: (context) => {
                        const value = context.parsed;
                        return `  Count : ${value}`;
                    },
                },
            },
        },
    };

    const chartSize = typeof height === "number" ? `${height}px` : height;

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            {/* Donut chart — centered */}
            <div style={{ height: chartSize, width: chartSize, position: "relative" }}>
                <Doughnut data={chartData} options={options} plugins={[centerTextPlugin]} />
            </div>

            {/* Legend — stacked below */}
            {legendItems && legendItems.length > 0 && (
                <div style={{
                    width: "100%",
                    marginTop: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}>
                    {legendItems.map((item, i) => (
                        <div
                            key={i}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px",
                            }}
                        >
                            {/* Circle dot */}
                            <div style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: item.color,
                                flexShrink: 0,
                            }} />

                            {/* Label — grows to fill space */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#6B7280",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}>
                                    {item.label}
                                </div>
                                {item.details && (
                                    <div style={{
                                        marginTop: "2px",
                                        fontSize: "12px",
                                        color: "#9CA3AF",
                                        lineHeight: 1.4,
                                        wordBreak: "break-word",
                                    }}>
                                        {item.details}
                                    </div>
                                )}
                            </div>

                            {/* Value — right-aligned */}
                            <span style={{
                                fontSize: "14px",
                                fontWeight: 700,
                                color: "#111827",
                                flexShrink: 0,
                                paddingTop: "1px",
                            }}>
                                {item.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DonutChart;
